import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { useTheme } from '../../theme'
import { isKey } from '../../utils'

import { Media, MediaImg, MediaBody } from '../media'

import getStyles from './styles'
import InfoIcon from './info-message-icon.svg'
import { UiVariant } from '../../types'

const variants = ['primary', 'secondary', 'positive', 'negative', 'alert', 'cta', 'brand', 'clear']

interface IInfoMessageProps {
  children: React.ReactNode
  border?: boolean
  flush?: boolean
  icon?: boolean
  style?: Rule
  variant?: UiVariant | 'clear'
}

export function InfoMessage({ children, variant, flush, style, icon, border }: IInfoMessageProps) {
  const styles = getStyles(useTheme())
  return (
    <article
      {...css([
        styles.container.base,
        isKey(styles.container.variants, variant) && styles.container.variants[variant],
        flush && styles.container.flush,
        border && styles.container.border,
        style,
      ])}
    >
      {icon ? (
        <Media>
          <MediaImg>
            <InfoIcon fill={isKey(styles.icon.variants, variant) ? styles.icon.variants[variant].color : undefined} />
          </MediaImg>
          <MediaBody>{children}</MediaBody>
        </Media>
      ) : (
        children
      )}
    </article>
  )
}

InfoMessage.propTypes = {
  border: PropTypes.bool,
  children: PropTypes.node.isRequired,
  flush: PropTypes.bool,
  icon: PropTypes.bool,
  style: PropTypes.object,
  variant: PropTypes.oneOf(variants),
}

InfoMessage.defaultProps = {
  border: false,
  flush: false,
  icon: false,
  variant: 'primary',
}

interface IInfoMessageTitleProps {
  children: React.ReactNode
  style?: Rule
}

export const InfoMessageTitle = ({ children, style = {} }: IInfoMessageTitleProps) => {
  const styles = getStyles(useTheme())
  return <header {...css({ ...styles.title.base, ...style })}>{children}</header>
}

InfoMessageTitle.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
}

InfoMessageTitle.displayName = 'InfoMessageTitle'

interface IInfoMessageDescriptionProps {
  children: React.ReactNode
  style?: Rule
}

export const InfoMessageDescription = ({ children, style = {} }: IInfoMessageDescriptionProps) => {
  const styles = getStyles(useTheme())
  return <div {...css([styles.description.base, style])}>{children}</div>
}

InfoMessageDescription.propTypes = {
  children: PropTypes.node.isRequired,
  wrapper: PropTypes.string,
  style: PropTypes.object,
}

InfoMessageDescription.displayName = 'InfoMessageDescription'

InfoMessage.VARIANTS = variants
